import JSZip from 'jszip';

export function isKmzUrl(url) {
  return /\.kmz(\?|$)/i.test(url || '');
}

export function parseKmlText(text) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'application/xml');
  if (xml.getElementsByTagName('parsererror').length) {
    throw new Error('Invalid KML XML');
  }

  const parseCoordString = (raw) =>
    raw
      .split(/\s+/)
      .map((c) => c.trim())
      .filter(Boolean)
      .map((triple) => {
        const parts = triple.split(',');
        const lng = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter(Boolean);

  const polygons = [];
  const addPolygonCoords = (coordEl) => {
    if (!coordEl) return;
    const coords = parseCoordString(coordEl.textContent || '');
    if (coords.length >= 3) polygons.push(coords);
  };

  const polyNodes = xml.getElementsByTagName('Polygon');
  for (let i = 0; i < polyNodes.length; i++) {
    const outer = polyNodes[i].getElementsByTagName('outerBoundaryIs')[0];
    if (outer) {
      addPolygonCoords(outer.getElementsByTagName('coordinates')[0]);
      continue;
    }
    addPolygonCoords(polyNodes[i].getElementsByTagName('coordinates')[0]);
  }

  const ringNodes = xml.getElementsByTagName('LinearRing');
  for (let i = 0; i < ringNodes.length; i++) {
    addPolygonCoords(ringNodes[i].getElementsByTagName('coordinates')[0]);
  }

  const lineNodes = xml.getElementsByTagName('LineString');
  for (let i = 0; i < lineNodes.length; i++) {
    const coordEl = lineNodes[i].getElementsByTagName('coordinates')[0];
    if (!coordEl) continue;
    const coords = parseCoordString(coordEl.textContent || '');
    if (coords.length >= 2) polygons.push(coords);
  }

  const markers = [];
  const pointNodes = xml.getElementsByTagName('Point');
  for (let i = 0; i < pointNodes.length; i++) {
    const coordEl = pointNodes[i].getElementsByTagName('coordinates')[0];
    if (!coordEl) continue;
    const coords = parseCoordString(coordEl.textContent || '');
    if (coords.length) markers.push(coords[0]);
  }

  const allPoints = [...polygons.flat(), ...markers];
  let center = null;
  let zoom = 4;
  if (allPoints.length) {
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    allPoints.forEach((p) => {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    });
    center = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
    const span = Math.max(maxLat - minLat, maxLng - minLng);
    if (span < 0.001) zoom = 17;
    else if (span < 0.01) zoom = 15;
    else if (span < 0.1) zoom = 12;
    else if (span < 1) zoom = 9;
    else if (span < 5) zoom = 7;
    else if (span < 20) zoom = 5;
    else zoom = 3;
  }

  return { polygons, markers, center, zoom };
}

export async function extractKmlTextFromKmzBuffer(buffer) {
  const zip = await JSZip.loadAsync(buffer);
  const kmlPaths = Object.keys(zip.files).filter((path) => {
    const entry = zip.files[path];
    return entry && !entry.dir && /\.kml$/i.test(path);
  });
  if (!kmlPaths.length) {
    throw new Error('No KML document found inside KMZ archive');
  }
  const docPath =
    kmlPaths.find((p) => /(^|\/)doc\.kml$/i.test(p)) ||
    kmlPaths.sort((a, b) => a.length - b.length)[0];
  return zip.file(docPath).async('string');
}

/** Green outline only — no fill, no dashed/stripe styling */
export const PROJECT_BOUNDARY_POLYGON_OPTIONS = {
  strokeColor: '#00BD73',
  strokeOpacity: 1,
  strokeWeight: 3,
  fillColor: '#00BD73',
  fillOpacity: 0,
  clickable: false,
  zIndex: 2,
};

/**
 * Ray-casting point-in-polygon (lat/lng objects with .lat / .lng).
 */
export function isPointInsidePolygon(lat, lng, polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const yi = polygon[i].lat;
    const xi = polygon[i].lng;
    const yj = polygon[j].lat;
    const xj = polygon[j].lng;
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** True if point is inside any project boundary ring; no boundary means allow all. */
export function isPointInsideProjectBoundary(lat, lng, polygons) {
  if (!Array.isArray(polygons) || polygons.length === 0) return true;
  const latN = parseFloat(lat);
  const lngN = parseFloat(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return false;
  return polygons.some((poly) => isPointInsidePolygon(latN, lngN, poly));
}

export async function fetchAndParseKmlFromUrl(url) {
  if (!url) {
    return { polygons: [], markers: [], center: null, zoom: 4 };
  }
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`Failed to fetch KML (${res.status})`);
  }
  const text = isKmzUrl(url)
    ? await extractKmlTextFromKmzBuffer(await res.arrayBuffer())
    : await res.text();
  return parseKmlText(text);
}
